import React, { useState } from 'react';
// @ts-ignore
import styles from 'WorkplaceHealthPlansSetup.module.scss';
import Loading from '../../../../../../Components/Loading';
import WorkplaceHealthPlansSetupItem, {
	IWorkplaceHealthPlansSetupItem,
} from './WorkplaceHealthPlansSetupItem';
import FormWrapper from '../../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useTranslation from '../../../../../../Hooks/useTranslation';
import {
	useCreateHealthPlanWorkplaceMutation,
	useDeleteHealthPlanWorkplaceMutation,
} from '../../../../../../graphql/graphql';
import { useAppContext } from '../../../../../../Services/AppContext';

interface Props {
	data: IWorkplaceHealthPlansSetupItem[];
	onUpdate: () => void;
}

const WorkplaceHealthPlansSetup: React.FC<Props> = ({ data, onUpdate }) => {
	const { t } = useTranslation();
	const { workplaceId } = useAppContext();
	const [isLoading, setIsLoading] = useState(false);
	const [createWorkplaceHealthPlan] = useCreateHealthPlanWorkplaceMutation();
	const [deleteWorkplaceHealthPlan] = useDeleteHealthPlanWorkplaceMutation();

	function generateDefaultValues(data: IWorkplaceHealthPlansSetupItem[]) {
		const defaultValues: Record<string, any> = {};

		data.forEach(item => {
			if (item.active) {
				defaultValues[`${item.id}_active`] = true;

				const consents: Record<string, boolean> = {};
				item.settings?.consentTerms?.forEach((term, index) => {
					consents[`${item.id}_consent_${index}`] = true;
				});

				defaultValues[`${item.id}_consents`] = consents;
			}
		});
		return defaultValues;
	}

	function generateYupSchema(data: IWorkplaceHealthPlansSetupItem[]) {
		const shape: Record<string, yup.SchemaOf<any>> = {};

		data.forEach(item => {
			const activeKey = `${item.id}_active`;
			const consentsKey = `${item.id}_consents`;
			shape[activeKey] = yup.boolean();
			shape[consentsKey] = yup
				.object()
				.test(
					'all-true-if-active',
					t('component.workplaceHealthPlansSetup.youMustAcceptAllConditions'),
					function (value) {
						const isActive = this.parent[activeKey];
						if (!isActive) return true;
						if (!value || typeof value !== 'object') return false;
						return Object.values(value).every(v => v === true);
					},
				)
				.nullable()
				.default({});
		});

		return yup.object().shape(shape);
	}

	const schema = generateYupSchema(data);

	const formMethods = useForm({
		defaultValues: generateDefaultValues(data),
		resolver: yupResolver(schema),
	});

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		const promises = data
			.filter(item => {
				//ACTUAL STATE IF DIFFERENT THAT STATE BEFORE
				return values[`${item.id}_active`] !== item.active;
			})
			.map(item => {
				const input = { healthPlanId: item.id, workplaceId };
				return values[`${item.id}_active`]
					? createWorkplaceHealthPlan({ input })
					: deleteWorkplaceHealthPlan({ input });
			});

		await Promise.allSettled(promises);
		await onUpdate();
		setIsLoading(false);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				{data?.map(healthPlan => {
					return (
						<WorkplaceHealthPlansSetupItem
							item={healthPlan}
							key={healthPlan.id}
						/>
					);
				})}
				<div className='mt-3'>
					<SubmitButton onSubmit={handleSubmit} title='base.shared.saveData' />
				</div>
			</FormWrapper>
		</div>
	);
};

export default WorkplaceHealthPlansSetup;
